import { Flex, type FlexProps, Heading, Text } from "@chakra-ui/react";
import {
  type UnifiedTokenBalance,
  formatCurrency,
  formatToNormalUnit,
  isTezosToken,
  tokenPrettyAmount,
  tokenSymbolSafe,
} from "@umami/chains";
import { useGetAssetPrice } from "@umami/state";

import { TokenIcon } from "../../assets/icons";
import { useColor } from "../../styles/useColor";

export const getTokenData = (token: UnifiedTokenBalance, _amount?: string) => {
  if (isTezosToken(token)) {
    const amount = _amount ?? token.balance;
    const prettyAmount = tokenPrettyAmount(amount, token);
    const symbol = tokenSymbolSafe(token);

    return {
      prettyAmount,
      symbol,
    };
  }

  return {
    prettyAmount: _amount
      ? formatToNormalUnit(_amount, token.chainId, token.decimals)
      : token.balance,
    symbol: token.symbol,
  };
};

export const TokenTile = ({
  token,
  amount,
  ...flexProps
}: { token: UnifiedTokenBalance; amount?: string } & FlexProps) => {
  const color = useColor();

  const { prettyAmount, symbol } = getTokenData(token, amount);
  const getAssetPrice = useGetAssetPrice();

  return (
    <Flex
      alignItems="center"
      justifyContent="start"
      width="full"
      padding="16px"
      background={color("100")}
      borderRadius="6px"
      data-testid="token-tile"
      {...flexProps}
    >
      <TokenIcon boxSize="42px" marginRight="8px" rounded="full" token={token} />
      <Flex flexDirection="column" gap="4px">
        <Flex alignItems="baseline">
          <Heading color={color("900")} data-testid="pretty-number" size="md">
            {prettyAmount}
          </Heading>
          <Text marginLeft="4px" color={color("900")} lineHeight="0" size="xl">
            {symbol}
          </Text>
        </Flex>
        <Text color={color("700")} size="sm">
          {formatCurrency(getAssetPrice(symbol) * Number(prettyAmount))}
        </Text>
      </Flex>
    </Flex>
  );
};
